import { Injectable } from '@angular/core';
import { HttpService } from './http.service';

/**
 * Per-user UI preference store.
 *
 * Currently backed by localStorage, namespaced by the logged-in admin's uuid so
 * two admins on the same machine never share preferences. The storage mechanism
 * is intentionally isolated behind this service — if we ever want these prefs to
 * follow a user across devices, only this file changes (swap localStorage for a
 * /admin/user/preference API), every caller stays the same.
 *
 * Scope = a stable string identifying the thing being remembered, e.g.
 *   'cols::agent-report'  -> hidden columns on the agent-report table
 *   'menu_favorites'      -> the sidebar features a user pinned
 */
@Injectable({ providedIn: 'root' })
export class PreferenceService {

  constructor(private http: HttpService) {}

  private currentUser(): string {
    try {
      const access: any = this.http.getAccess();
      return (access && access.uuid) ? access.uuid : 'anon';
    } catch {
      return 'anon';
    }
  }

  private keyFor(scope: string): string {
    return `pref::${this.currentUser()}::${scope}`;
  }

  public get<T>(scope: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(this.keyFor(scope));
      if (raw === null || raw === undefined) return fallback;
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }

  public set(scope: string, value: any): void {
    try {
      localStorage.setItem(this.keyFor(scope), JSON.stringify(value));
    } catch {
      // storage full / disabled — preferences are best-effort, never block the UI
    }
  }

  public remove(scope: string): void {
    try {
      localStorage.removeItem(this.keyFor(scope));
    } catch {
      // no-op
    }
  }
}
